import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Heart } from 'lucide-react';
import { useState, useEffect } from 'react';

const footerLinks = {
  services: [
    { name: 'Scrap & Factory Management', href: '/services/integrated-waste-management' },
    { name: 'Plastic Waste Management', href: '/services/recycling-solutions' },
    { name: 'E-Waste Management', href: '/services/recycling-solutions' },
    { name: 'EPR Services', href: '/services/compliance-epr' },
    { name: 'Expired Stock Disposal', href: '/services/resource-recovery' },
    { name: 'ESG Reporting', href: '/services/esg-reporting' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Our Impact', href: '/impact' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ],
};

const CURRENT_YEAR = 2026;

export function Footer() {
  const [year, setYear] = useState(CURRENT_YEAR);
  useEffect(() => { setYear(new Date().getFullYear()); }, []);

  return (
    <footer className="bg-gray-950 text-white">
      {/* Donate / Community Banner */}
      <div className="bg-p-700 border-b border-p-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-white">
              <Heart className="w-4 h-4 text-p-200 shrink-0" />
              <span>Donate your old garments, toys, or e-waste — we'll ensure responsible recovery.</span>
            </div>
            <Link
              to="/contact?donate=true"
              className="shrink-0 px-4 py-1.5 bg-white text-p-700 text-sm font-semibold rounded-md hover:bg-p-50 transition-colors"
            >
              Donate Waste →
            </Link>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2.5 mb-4">
              <picture>
                <source srcSet="/images/logo.webp" type="image/webp" />
                <img src="/images/logo.png" alt="Punarvaa" width={36} height={36} className="w-9 h-9 object-contain" />
              </picture>
              <div className="flex flex-col leading-none">
                <span className="text-base font-bold text-white">Punarvaa</span>
                <span className="text-[10px] text-gray-500">by Patekar Recycle</span>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Structured waste recovery systems for businesses, cities, and communities across India.
              Waste → Resource → Value.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.linkedin.com/company/junkanix/" target="_blank" rel="noopener noreferrer"
                className="text-gray-500 hover:text-white transition-colors text-sm font-medium">LinkedIn</a>
              <a href="https://www.instagram.com/p/DWnwZQFDA13/?igsh=MWl0NnNjczI4Nmg4eg%3D%3D" target="_blank" rel="noopener noreferrer"
                className="text-gray-500 hover:text-white transition-colors text-sm font-medium">Instagram</a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">Vahuli Village, Vadpe Bhiwandi,<br />Thane, Maharashtra – 421302</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-600 shrink-0" />
                <a href="tel:+919320937373" className="text-gray-400 hover:text-white transition-colors text-sm">
                  +91 93209 37373
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <a href="mailto:info@patekarecycle.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                    info@patekarecycle.com
                  </a>
                  <a href="mailto:connect@patekarecycle.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                    connect@patekarecycle.com
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-gray-600 text-xs">
              © {year} Patekar Recycle Solution Pvt Ltd. All rights reserved.
            </p>
            <p className="text-gray-700 text-xs">
              Designed & developed by{' '}
              <a
                href="https://bloomcodetechnologies.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-300 transition-colors"
              >
                Bloomcode Technologies
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
